import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"

/** Trigger save + loader and add new slide when visible rows reach this count (not before). */
const EXPAND_AFTER_ROW_COUNT = 22

export default class extends Controller {
  static values = {
    bookId: String,
    bookUuid: String,
    optionAPlaceholder: String,
    optionBPlaceholder: String,
    moveLeft: String,
    moveRight: String,
    deleteChoice: String,
    maxPerSlide: { type: Number, default: 20 },
  }

  declare bookIdValue: string
  declare bookUuidValue: string
  declare optionAPlaceholderValue: string
  declare optionBPlaceholderValue: string
  declare moveLeftValue: string
  declare moveRightValue: string
  declare deleteChoiceValue: string
  declare maxPerSlideValue: number

  private formElement: HTMLFormElement | null = null
  private nextNewIndex: number = 0
  private isSaving: boolean = false
  private isAdding: boolean = false
  /** True only after user adds a choice or edits option_a/option_b. Used to avoid saving on slide change when nothing changed. */
  private formDirty: boolean = false

  connect() {
    this.ensureFormElement()
    if (!this.formElement) {
      console.error(
        "Voting choices form not found. Element:",
        this.element,
        "Parent:",
        this.element.parentElement,
      )
      return
    }
    this.calculateNextNewIndex()
    this.tryAutoAddChoiceOnLoad()
    document.addEventListener("turbo:before-stream-render", this.handleStreamRender.bind(this))
    document.addEventListener("book-slider:saveVotingChoices", this.saveOnSlideChange.bind(this))
    document.addEventListener("saveBeforeNavigate", this.saveBeforeNavigate.bind(this))
  }

  private tryAutoAddChoiceOnLoad() {
    // Only auto-open when the server rendered the "add" affordance (last chunk).
    const addButton = this.element.querySelector("#add-choice-btn")
    if (!addButton) return

    if ((this.element as HTMLElement).dataset.votingChoicesAutoAdd === "1") return

    // Slick applies `slick-active` slightly after initialization; check a few times.
    const delays = [0, 80, 180]
    for (const delay of delays) {
      window.setTimeout(() => {
        if ((this.element as HTMLElement).dataset.votingChoicesAutoAdd === "1") return
        this.autoAddChoiceRowIfNeeded()
      }, delay)
    }
  }

  private autoAddChoiceRowIfNeeded() {
    const addButton = this.element.querySelector("#add-choice-btn")
    if (!addButton) return

    const container = this.element.querySelector(".voting-choices-container") as HTMLElement | null
    if (!container || !this.formElement) return

    this.calculateNextNewIndex()

    // If we already inserted a blank row (data-choice-id="new_*"), just focus it.
    const existingNewRow = container.querySelector(
      ".voting-choice-row[data-choice-id^='new_']",
    ) as HTMLElement | null
    if (existingNewRow) {
      ;(this.element as HTMLElement).dataset.votingChoicesAutoAdd = "1"
      return
    }

    const visibleRows = Array.from(
      container.querySelectorAll(".voting-choice-row:not([style*='display: none'])"),
    ) as HTMLElement[]

    if (visibleRows.length === 0) {
      const newIndex = this.nextNewIndex
      this.nextNewIndex++
      this.insertEmptyChoiceRow(container, newIndex)
      ;(this.element as HTMLElement).dataset.votingChoicesAutoAdd = "1"
      return
    }

    // If any existing row is partially empty, keep it as current add-box state.
    for (const row of visibleRows) {
      const optionAInput = row.querySelector('input[name*="[option_a]"]') as HTMLInputElement | null
      const optionBInput = row.querySelector('input[name*="[option_b]"]') as HTMLInputElement | null

      const optionA = optionAInput?.value?.trim() ?? ""
      const optionB = optionBInput?.value?.trim() ?? ""

      if (!optionA && optionAInput) {
        ;(this.element as HTMLElement).dataset.votingChoicesAutoAdd = "1"
        return
      }
      if (!optionB && optionBInput) {
        ;(this.element as HTMLElement).dataset.votingChoicesAutoAdd = "1"
        return
      }
    }

    // All visible rows have both options filled -> reveal the next blank row.
    const allFilled = visibleRows.every((row) => {
      const optionA =
        (
          row.querySelector('input[name*="[option_a]"]') as HTMLInputElement | null
        )?.value?.trim() ?? ""
      const optionB =
        (
          row.querySelector('input[name*="[option_b]"]') as HTMLInputElement | null
        )?.value?.trim() ?? ""
      return optionA.length > 0 && optionB.length > 0
    })

    if (allFilled) {
      const newIndex = this.nextNewIndex
      this.nextNewIndex++
      this.insertEmptyChoiceRow(container, newIndex)
    }

    ;(this.element as HTMLElement).dataset.votingChoicesAutoAdd = "1"
  }

  private insertEmptyChoiceRow(container: HTMLElement, index: number) {
    const newChoiceRow = this.createChoiceRow(index, "", "")
    container.insertAdjacentHTML("beforeend", newChoiceRow)
  }

  private ensureFormElement(): void {
    if (
      this.formElement &&
      document.contains(this.formElement) &&
      this.formElement.contains(this.element)
    ) {
      return
    }
    this.formElement = this.element.closest("form") as HTMLFormElement
  }

  disconnect() {
    document.removeEventListener("turbo:before-stream-render", this.handleStreamRender.bind(this))
    document.removeEventListener("book-slider:saveVotingChoices", this.saveOnSlideChange.bind(this))
    document.removeEventListener("saveBeforeNavigate", this.saveBeforeNavigate.bind(this))
  }

  private saveBeforeNavigate(event: Event) {
    const customEvent = event as CustomEvent<{ promises: Promise<unknown>[] }>
    if (!customEvent.detail?.promises) return
    if (!this.formDirty) return
    if (!this.hasAtLeastOneFilledChoice() && !this.hasPendingDestroy()) return
    this.ensureFormElement()
    if (this.formElement) {
      customEvent.detail.promises.push(this.saveForm({ userTriggered: false }))
    }
  }

  private saveOnSlideChange() {
    if (!this.formDirty) return
    if (!this.hasAtLeastOneFilledChoice() && !this.hasPendingDestroy()) return
    this.saveForm({ userTriggered: false })
  }

  private hasPendingDestroy(): boolean {
    const form = this.formElement
    if (!form) return false
    const destroyInputs = form.querySelectorAll('input[name*="[_destroy]"][value="1"]')
    return destroyInputs.length > 0
  }

  private isOnLastTwoSidedVotingSlide(): boolean {
    const form = this.formElement
    if (!form) return false
    const allForms = document.querySelectorAll(
      '[data-slide-type^="two-sided-voting"] form.voting-choices-form',
    )
    if (allForms.length === 0) return false
    const lastForm = allForms[allForms.length - 1]
    return lastForm === form || lastForm.contains(this.element)
  }

  markDirty() {
    this.formDirty = true
  }

  private hasAtLeastOneFilledChoice(): boolean {
    const container = this.element.querySelector(
      ".voting-choices-container, #voting-choices-container",
    )
    if (!container) return false
    const rows = container.querySelectorAll(".voting-choice-row:not([style*='display: none'])")
    for (const row of rows) {
      const optionA =
        (row.querySelector('input[name*="[option_a]"]') as HTMLInputElement)?.value?.trim() ?? ""
      const optionB =
        (row.querySelector('input[name*="[option_b]"]') as HTMLInputElement)?.value?.trim() ?? ""
      if (optionA.length > 0 && optionB.length > 0) return true
    }
    return false
  }

  private showExpandLoader(): HTMLElement {
    const slide =
      this.element.closest(".slide") ||
      this.element.closest('[data-slide-type^="two-sided-voting"]')
    const container = slide || this.element
    if (!(container instanceof HTMLElement)) return document.createElement("div")

    const wrapper = document.createElement("div")
    wrapper.className = "voting-choices-expand-loader"
    wrapper.setAttribute("aria-live", "polite")
    wrapper.setAttribute("role", "status")
    wrapper.style.cssText =
      "position:absolute;inset:0;background:rgba(255,255,255,0.92);display:flex;flex-direction:column;align-items:center;justify-content:center;gap:1rem;z-index:20;border-radius:inherit;"
    wrapper.innerHTML = `
      <div class="spinner-border text-primary" role="status" style="width:2.5rem;height:2.5rem;">
        <span class="visually-hidden">Loading</span>
      </div>
      <p class="mb-0 small fw-medium text-secondary">Saving choices and adding new page…</p>
    `
    const style = container.style.position
    if (!style || style === "static") container.style.position = "relative"
    container.appendChild(wrapper)
    return wrapper
  }

  private handleStreamRender(_event: CustomEvent) {
    setTimeout(() => {
      this.calculateNextNewIndex()
    }, 100)
  }

  private calculateNextNewIndex() {
    if (!this.formElement) return

    const container = this.element.querySelector(".voting-choices-container")
    if (!container) {
      this.nextNewIndex = 0
      return
    }

    const visibleRows = Array.from(
      container.querySelectorAll(".voting-choice-row:not([style*='display: none'])"),
    )
    const usedIndices = new Set<number>()

    visibleRows.forEach((row) => {
      const inputs = row.querySelectorAll('input[name*="[two_sided_voting_choices_attributes]"]')
      inputs.forEach((input) => {
        const name = (input as HTMLInputElement).name
        const match = name.match(/\[two_sided_voting_choices_attributes\]\[(\d+)\]/)
        if (match) {
          const index = parseInt(match[1], 10)
          usedIndices.add(index)
        }
      })
    })

    let nextIndex = 0
    while (usedIndices.has(nextIndex)) {
      nextIndex++
    }

    this.nextNewIndex = nextIndex
  }

  maybeAddEmptyRow() {
    if (this.isAdding || this.isSaving) return

    const container = this.element.querySelector("#voting-choices-container")
    if (!container) return

    const visibleRows = Array.from(
      container.querySelectorAll(".voting-choice-row:not([style*='display: none'])"),
    )
    if (visibleRows.length === 0) return

    const allFilled = visibleRows.every((row) => {
      const optionA =
        (row.querySelector('input[name*="[option_a]"]') as HTMLInputElement)?.value?.trim() ?? ""
      const optionB =
        (row.querySelector('input[name*="[option_b]"]') as HTMLInputElement)?.value?.trim() ?? ""
      return optionA.length > 0 && optionB.length > 0
    })

    if (allFilled) {
      this.addChoice()
    }
  }

  async addChoice() {
    if (this.isAdding || this.isSaving) return

    this.ensureFormElement()
    const container = this.element.querySelector(".voting-choices-container")
    if (!container || !this.formElement) return

    this.calculateNextNewIndex()

    const existingRows = container.querySelectorAll(".voting-choice-row")
    for (const row of existingRows) {
      const htmlRow = row as HTMLElement
      const optionAInput = row.querySelector('input[name*="[option_a]"]') as HTMLInputElement
      const optionBInput = row.querySelector('input[name*="[option_b]"]') as HTMLInputElement

      if (optionAInput && optionBInput) {
        const optionA = optionAInput.value.trim()
        const optionB = optionBInput.value.trim()

        if (!optionA && !optionB && htmlRow.style.display !== "none") {
          htmlRow.scrollIntoView({ block: "nearest", behavior: "smooth" })
          optionAInput.focus()
          return
        }
      }
    }

    this.isAdding = true
    this.formDirty = true

    try {
      const newIndex = this.nextNewIndex
      this.nextNewIndex++
      const newChoiceRow = this.createChoiceRow(newIndex, "", "")

      container.insertAdjacentHTML("beforeend", newChoiceRow)

      const newRow = container.querySelector(`[data-choice-id="new_${newIndex}"]`) as HTMLElement
      if (newRow) {
        const firstInput = newRow.querySelector('input[name*="[option_a]"]') as HTMLInputElement
        if (firstInput) {
          const countAfterAdd = this.getVisibleRowCount()
          const onLastSlide = this.isOnLastTwoSidedVotingSlide()
          const shouldExpand = countAfterAdd >= EXPAND_AFTER_ROW_COUNT || onLastSlide

          if (shouldExpand) {
            ;(
              window as Window & { b4mGoToLastTwoSidedVotingAfterReload?: boolean }
            ).b4mGoToLastTwoSidedVotingAfterReload = true
            const loaderEl = this.showExpandLoader()
            await this.saveForm({
              expandVotingSlide: true,
              loaderElement: loaderEl,
            })
          } else {
            setTimeout(() => firstInput.focus(), 10)
          }
        }
      }
    } finally {
      this.isAdding = false
    }
  }

  deleteChoice(event: Event) {
    const button = event.currentTarget as HTMLElement
    const row = button.closest(".voting-choice-row") as HTMLElement | null
    if (!row) return

    const choiceId = row.getAttribute("data-choice-id") || button.getAttribute("data-choice-id")
    if (!choiceId) return

    if (choiceId && !choiceId.startsWith("new_")) {
      this.preserveDestroyInForm(row, choiceId)
      row.remove()
    } else {
      const index = choiceId.replace("new_", "")
      const allFields = this.formElement?.querySelectorAll(
        `input[name*="[two_sided_voting_choices_attributes][${index}]"]`,
      )
      if (allFields) {
        allFields.forEach((field) => {
          field.remove()
        })
      }

      row.remove()
    }

    this.calculateNextNewIndex()
    this.formDirty = true
  }

  private preserveDestroyInForm(row: HTMLElement, choiceId: string) {
    if (!this.formElement) return
    const anyInput = row.querySelector(
      'input[name*="[two_sided_voting_choices_attributes]"]',
    ) as HTMLInputElement
    if (!anyInput) return
    const match = anyInput.name.match(/\[two_sided_voting_choices_attributes\]\[(\d+)\]/)
    const index = match ? match[1] : "0"

    let container = this.formElement.querySelector(
      ".voting-choices-pending-destroys",
    ) as HTMLElement
    if (!container) {
      container = document.createElement("div")
      container.className = "voting-choices-pending-destroys"
      container.setAttribute("aria-hidden", "true")
      container.style.display = "none"
      this.formElement.appendChild(container)
    }

    const idInput = document.createElement("input")
    idInput.type = "hidden"
    idInput.name = `book[two_sided_voting_choices_attributes][${index}][id]`
    idInput.value = choiceId
    container.appendChild(idInput)

    const destroyInput = document.createElement("input")
    destroyInput.type = "hidden"
    destroyInput.name = `book[two_sided_voting_choices_attributes][${index}][_destroy]`
    destroyInput.value = "1"
    container.appendChild(destroyInput)
  }

  moveLeft(event: Event) {
    const button = event.currentTarget as HTMLElement
    const row = button.closest(".voting-choice-row")
    if (!row) return

    const container = this.element.querySelector(".voting-choices-container")
    if (!container) return

    const prevRow = row.previousElementSibling
    if (!prevRow || !(prevRow instanceof HTMLElement) || prevRow.style.display === "none") return

    container.insertBefore(row, prevRow)
    this.reorderFormFieldsToMatchDOM()
    this.formDirty = true
  }

  moveRight(event: Event) {
    const button = event.currentTarget as HTMLElement
    const row = button.closest(".voting-choice-row")
    if (!row) return

    const container = this.element.querySelector(".voting-choices-container")
    if (!container) return

    const nextRow = row.nextElementSibling as HTMLElement
    if (!nextRow || nextRow.style.display === "none") return

    container.insertBefore(nextRow, row)
    this.reorderFormFieldsToMatchDOM()
    this.formDirty = true
  }

  private createChoiceRow(index: number, optionA: string, optionB: string): string {
    return `
      <div class="voting-choice-row flex items-center gap-2 md:gap-3 max-w-full min-w-0" data-choice-id="new_${index}">
        <input type="hidden" name="book[two_sided_voting_choices_attributes][${index}][_destroy]" value="0" class="destroy-field">
        <input
          type="text"
          name="book[two_sided_voting_choices_attributes][${index}][option_a]"
          placeholder="${this.escapeHtml(this.optionAPlaceholderValue)}"
          value="${this.escapeHtml(optionA)}"
          class="flex-1 min-w-0 bg-gray-200 hover:bg-gray-300 rounded-lg px-3 md:px-4 py-2.5 md:py-3 text-sm md:text-base font-medium text-gray-600 text-center transition-colors border-0 focus:ring-2 focus:ring-gray-400 focus:outline-none cursor-text"
          style="appearance: none; -webkit-appearance: none;"
          data-action="blur->voting-choices#maybeAddEmptyRow input->voting-choices#markDirty"
        >
        <button
          type="button"
          class="shrink-0 text-gray-600 hover:text-gray-900 transition-colors bg-transparent border-0 cursor-pointer"
          data-action="click->voting-choices#moveLeft"
          title="${this.escapeHtml(this.moveLeftValue)}"
        >
          <i class="bi bi-chevron-left"></i>
        </button>
        <button
          type="button"
          class="shrink-0 text-gray-600 hover:text-gray-900 transition-colors bg-transparent border-0 cursor-pointer"
          data-action="click->voting-choices#deleteChoice"
          data-choice-id="new_${index}"
          title="${this.escapeHtml(this.deleteChoiceValue)}"
        >
          <i class="bi bi-trash3"></i>
        </button>
        <button
          type="button"
          class="shrink-0 text-gray-600 hover:text-gray-900 transition-colors bg-transparent border-0 cursor-pointer"
          data-action="click->voting-choices#moveRight"
          title="${this.escapeHtml(this.moveRightValue)}"
        >
          <i class="bi bi-chevron-right"></i>
        </button>
        <input
          type="text"
          name="book[two_sided_voting_choices_attributes][${index}][option_b]"
          placeholder="${this.escapeHtml(this.optionBPlaceholderValue)}"
          value="${this.escapeHtml(optionB)}"
          class="flex-1 min-w-0 bg-gray-200 hover:bg-gray-300 rounded-lg px-3 md:px-4 py-2.5 md:py-3 text-sm md:text-base font-medium text-gray-600 text-center transition-colors border-0 focus:ring-2 focus:ring-gray-400 focus:outline-none cursor-text"
          style="appearance: none; -webkit-appearance: none;"
          data-action="blur->voting-choices#maybeAddEmptyRow input->voting-choices#markDirty"
        >
      </div>
    `
  }

  private getVisibleRowCount(): number {
    const container = this.element.querySelector(".voting-choices-container")
    if (!container) return 0

    return Array.from(
      container.querySelectorAll(".voting-choice-row:not([style*='display: none'])"),
    ).length
  }

  private updateFormField(choiceId: string | null, fieldName: string, value: string) {
    if (!this.formElement || !choiceId) return

    const allFields = this.formElement.querySelectorAll(
      `input[name*="[two_sided_voting_choices_attributes]"]`,
    )

    if (choiceId.startsWith("new_")) {
      const index = choiceId.replace("new_", "")
      allFields.forEach((field) => {
        const input = field as HTMLInputElement
        const name = input.name
        if (name.includes(`[${index}][${fieldName}]`)) {
          input.value = value
        }
      })
    } else {
      const idFields = Array.from(allFields).filter((f) => {
        const name = (f as HTMLInputElement).name
        return name.includes("[id]")
      }) as HTMLInputElement[]

      idFields.forEach((idField) => {
        if (idField.value === choiceId) {
          const match = idField.name.match(/\[(\d+)\]/)
          if (match) {
            const index = match[1]
            allFields.forEach((field) => {
              const input = field as HTMLInputElement
              const name = input.name
              if (name.includes(`[${index}][${fieldName}]`)) {
                input.value = value
              }
            })
          }
        }
      })
    }
  }

  private removeFormFields(choiceId: string | null) {
    if (!this.formElement || !choiceId) return

    const allFields = this.formElement.querySelectorAll(
      `input[name*="[two_sided_voting_choices_attributes]"]`,
    )

    allFields.forEach((field) => {
      const input = field as HTMLInputElement
      const name = input.name

      if (choiceId.startsWith("new_")) {
        const index = choiceId.replace("new_", "")
        if (name.includes(`[${index}]`)) {
          input.remove()
        }
      }
    })
  }

  private reorderFormFieldsToMatchDOM() {
    if (!this.formElement) return

    const container = this.element.querySelector(".voting-choices-container")
    if (!container) return

    const allRows = Array.from(container.querySelectorAll(".voting-choice-row"))
    const visibleRows = allRows.filter((row) => (row as HTMLElement).style.display !== "none")
    const hiddenRows = allRows.filter((row) => (row as HTMLElement).style.display === "none")

    // Assign indices so visible rows are 0..visibleLength-1, then hidden (to-destroy) rows
    // get the next indices. This avoids duplicate param keys so _destroy is never overwritten.
    const assignIndex = (row: Element, newIndex: number) => {
      const rowFields = row.querySelectorAll('input[name*="[two_sided_voting_choices_attributes]"]')
      rowFields.forEach((input) => {
        const htmlInput = input as HTMLInputElement
        const oldName = htmlInput.name
        const match = oldName.match(/\[two_sided_voting_choices_attributes\]\[\d+\](.+)$/)
        if (match) {
          const fieldName = match[1]
          htmlInput.name = `book[two_sided_voting_choices_attributes][${newIndex}]${fieldName}`
        }
      })
    }

    visibleRows.forEach((row, i) => {
      assignIndex(row, i)
    })
    hiddenRows.forEach((row, i) => {
      assignIndex(row, visibleRows.length + i)
    })
  }

  private async saveForm(
    userTriggeredOrOptions:
      | boolean
      | {
          userTriggered?: boolean
          expandVotingSlide?: boolean
          loaderElement?: HTMLElement | null
        } = true,
  ) {
    this.ensureFormElement()
    if (!this.formElement) {
      console.error("saveForm: formElement is null")
      return
    }

    if (this.isSaving) return

    if (!this.hasAtLeastOneFilledChoice() && !this.hasPendingDestroy()) return

    const options =
      typeof userTriggeredOrOptions === "boolean"
        ? { userTriggered: userTriggeredOrOptions }
        : userTriggeredOrOptions
    const userTriggered = options.userTriggered !== false
    const expandVotingSlide = options.expandVotingSlide === true
    const loaderElement = options.loaderElement ?? null

    this.reorderFormFieldsToMatchDOM()

    this.isSaving = true

    const formData = new FormData(this.formElement)
    if (expandVotingSlide) {
      formData.append("expand_voting_slide", "1")
    }

    try {
      const response = await fetch(this.formElement.action, {
        method: this.formElement.method || "POST",
        headers: {
          Accept: "text/vnd.turbo-stream.html, text/html, application/xhtml+xml",
          "X-CSRF-Token": this.getCSRFToken(),
          "X-Requested-With": "XMLHttpRequest",
        },
        body: formData,
      })

      if (response.ok) {
        const html = await response.text()
        if (html?.includes("turbo-stream")) {
          Turbo.renderStreamMessage(html)
        }
        const pendingDestroys = this.formElement?.querySelector(".voting-choices-pending-destroys")
        pendingDestroys?.replaceChildren()
      } else if (userTriggered) {
        console.error("Error saving form:", response.status, response.statusText)
        this.showFlash("Error saving choices. Please try again.", "danger")
      }
    } catch (error) {
      console.error("Error saving form:", error)
      if (userTriggered) {
        this.showFlash("Error saving choices. Please try again.", "danger")
      }
    } finally {
      this.isSaving = false
      if (!options.expandVotingSlide) {
        this.formDirty = false
      }
      if (loaderElement?.parentNode) {
        loaderElement.remove()
      }
      document.dispatchEvent(new CustomEvent("voting-choices:saved"))
    }
  }

  private getCSRFToken(): string {
    const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content")
    if (!token) {
      console.warn("CSRF token not found")
      return ""
    }
    return token
  }

  private escapeHtml(text: string): string {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }

  private showFlash(message: string, variant: "success" | "danger" = "success") {
    const main = document.querySelector("main#main")
    if (!main) return

    const existingAlerts = main.querySelectorAll(".alert[data-turbo-temporary]")
    existingAlerts.forEach((alert) => {
      alert.remove()
    })

    const alert = document.createElement("div")
    alert.className = `mb-org3 alert alert-${variant} alert-dismissible fade show zIndex1`
    alert.setAttribute("role", "alert")
    alert.setAttribute("data-turbo-temporary", "true")
    alert.innerHTML = `
      ${this.escapeHtml(message)}
      <button type="button" class="btn-close" data-bs-dismiss="alert" aria-label="Close"></button>
    `

    main.insertBefore(alert, main.firstChild)

    setTimeout(() => {
      if (alert.parentNode) {
        alert.classList.remove("show")
        setTimeout(() => alert.remove(), 150)
      }
    }, 3000)
  }
}
